import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Invite() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');
  const inviteEmail = searchParams.get('email');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteRedeemed, setInviteRedeemed] = useState(false);

  useEffect(() => {
    // Auto-redeem invite if code and email are present
    if (inviteCode && inviteEmail && !inviteRedeemed) {
      redeemInvite();
    }
    
    // If user is already logged in and invite is redeemed, show success message (don't redirect)
    // This allows logged-in users to see the invite page
  }, [inviteCode, inviteEmail]);

  const redeemInvite = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}/api/invite/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          invite_code: inviteCode
        })
      });

      if (!response.ok) {
        throw new Error('Invalid or expired invite code');
      }

      const data = await response.json();
      console.log('✅ Invite redeemed:', data);
      setInviteRedeemed(true);
      
    } catch (err) {
      console.error('❌ Error redeeming invite:', err);
      setError(err.message || 'Failed to redeem invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
      }}
    >
      {/* Animated Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed inset-0 w-full h-full object-cover"
        style={{ 
          zIndex: 0,
          filter: 'brightness(0.7) saturate(1.2)'
        }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
        <img 
          src="/assets/nuke-ride-bg.png" 
          alt="Background"
          className="w-full h-full object-cover"
        />
      </video>

      {/* Dark overlay */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        style={{ zIndex: 1 }}
      />
      
      {/* Content */}
      <div 
        className="relative bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-md p-10 rounded-xl w-full max-w-2xl border-4 border-orange-500/50 shadow-2xl"
        style={{
          zIndex: 2,
          boxShadow: '0 0 60px rgba(255, 107, 53, 0.5), 0 0 120px rgba(255, 107, 53, 0.3), inset 0 2px 20px rgba(0,0,0,0.5)'
        }}
      >
        {/* GTNW3 Characters Badge */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/4i8xee4n_GTNW3-Characters-LOGO.PNG"
            alt="GTNW3"
            className="w-full max-w-md mx-auto rounded-lg"
            style={{
              filter: 'drop-shadow(0 0 30px rgba(255, 107, 53, 0.6))'
            }}
          />
        </div>

        <h1 className="text-5xl font-bold text-center mb-4 font-mono tracking-wider"
          style={{
            background: 'linear-gradient(to right, #ff6b35, #fbbf24)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 30px rgba(255, 107, 53, 0.6)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.9))',
          }}
        >
          ☢️ YOU'RE INVITED ☢️
        </h1>
        
        <p className="text-center text-slate-300 mb-8 text-lg tracking-wide">
          Exclusive Beta Access to GTNW3
        </p>

        {/* VIP Badge */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 mb-8 text-center border-2 border-yellow-400"
          style={{
            boxShadow: '0 0 25px rgba(251, 191, 36, 0.5)'
          }}
        >
          <p className="text-3xl font-bold text-black mb-2">
            👑 VIP ACCESS GRANTED 👑
          </p>
          <p className="text-sm text-black/80">
            Unlimited games • Exclusive features • Priority support
          </p>
        </div>

        {loading && (
          <div className="bg-blue-500/20 border border-blue-500 text-blue-200 px-4 py-3 rounded mb-6 text-center">
            <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full mr-3"></div>
            Activating your VIP status...
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {inviteRedeemed && (
          <div className="bg-green-500/20 border border-green-500 text-green-200 px-4 py-3 rounded mb-6 text-center">
            ✅ Invite accepted! Your VIP status is ready.
          </div>
        )}

        <div className="space-y-6 text-slate-300">
          <p className="text-center text-lg">
            You've been invited to participate in the <strong className="text-orange-400">GLOBAL THERMONUCLEAR WAR ]|[ Beta</strong> by <strong className="text-yellow-400">Kaj</strong>.
          </p>
          
          <div className="bg-slate-800/80 border-2 border-blue-500/50 rounded-lg p-5">
            <p className="text-blue-300 font-semibold mb-3 text-center">💻 Best Played On:</p>
            <p className="text-center"><strong>Mac or PC</strong> (Desktop/Laptop recommended)</p>
            <p className="text-sm text-slate-400 text-center mt-2">📱 Future versions will have iOS support</p>
          </div>

          <p className="text-center text-xl font-bold text-yellow-400">
            We're excited to have you on board, Commander!
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="mt-8 space-y-4">
          {user ? (
            /* User is already logged in */
            <div className="space-y-4">
              <div className="bg-green-500/20 border border-green-500 text-green-200 px-6 py-4 rounded-lg text-center">
                <p className="text-lg font-bold mb-2">✅ You're already logged in!</p>
                <p className="text-sm">Your VIP status is active. Ready to play?</p>
              </div>
              <Link
                to="/"
                className="block w-full bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold py-4 px-8 rounded-lg hover:from-orange-700 hover:to-red-700 transition-all text-lg text-center uppercase tracking-wide"
                style={{
                  boxShadow: '0 6px 25px rgba(255, 107, 53, 0.5)'
                }}
              >
                ☢️ Go to Game ☢️
              </Link>
            </div>
          ) : (
            /* User is not logged in - show signup/login buttons */
            <>
              <Link
                to="/signup"
                className="block w-full bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold py-4 px-8 rounded-lg hover:from-orange-700 hover:to-red-700 transition-all text-lg text-center uppercase tracking-wide"
                style={{
                  boxShadow: '0 6px 25px rgba(255, 107, 53, 0.5)'
                }}
              >
                ☢️ Create Account & Start Playing ☢️
              </Link>
              
              <Link
                to="/login"
                className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-center"
              >
                Already have an account? Login
              </Link>
            </>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          This invitation grants you permanent VIP access
        </p>
      </div>
    </div>
  );
}
